import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ProductGalleryImage {
  src: string;
  alt: string;
  title?: string;
}

interface ProductGalleryProps {
  images: ProductGalleryImage[];
  className?: string;
  aspect?: "square" | "4/3" | "16/9";
}

/**
 * Mobile-friendly product image gallery.
 * - Swipe navigation on touch (Embla)
 * - Tap/click to open full-screen lightbox with pinch-to-zoom (native)
 * - Arrow keys + on-screen arrows (desktop)
 * - Thumbnail strip syncs with active slide
 */
export const ProductGallery = ({ images, className, aspect = "square" }: ProductGalleryProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const openLightbox = (i: number) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };
  const lbPrev = useCallback(
    () => setLightboxIndex((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );
  const lbNext = useCallback(
    () => setLightboxIndex((i) => (i + 1) % images.length),
    [images.length]
  );

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") lbPrev();
      if (e.key === "ArrowRight") lbNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, lbPrev, lbNext]);

  if (!images.length) return null;

  const aspectClass =
    aspect === "16/9" ? "aspect-video" : aspect === "4/3" ? "aspect-[4/3]" : "aspect-square";

  return (
    <div className={cn("relative", className)}>
      {/* Carousel */}
      <div
        className="relative overflow-hidden rounded-xl border border-border/40 bg-muted/20 shadow-elegant"
        ref={emblaRef}
      >
        <div className="flex touch-pan-y">
          {images.map((img, i) => (
            <figure key={`${img.src}-${i}`} className="relative m-0 min-w-0 flex-[0_0_100%]">
              <button
                type="button"
                onClick={() => openLightbox(i)}
                className="group block w-full"
                aria-label={`Open ${img.alt} in full screen`}
              >
                <div className={cn("relative bg-muted/30", aspectClass, "sm:aspect-[4/3]")}>
                  <img
                    src={img.src}
                    alt={img.alt}
                    title={img.title || img.alt}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    draggable={false}
                    className="absolute inset-0 h-full w-full select-none object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  <div className="absolute right-3 top-3 rounded-full bg-background/80 p-2 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 sm:opacity-0">
                    <ZoomIn className="h-4 w-4 text-foreground" />
                  </div>
                  {img.title && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 sm:p-4">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                        {img.title}
                      </span>
                    </figcaption>
                  )}
                </div>
              </button>
            </figure>
          ))}
        </div>

        {/* Arrows (desktop) */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={scrollPrev}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-border bg-background/90 p-2 text-foreground shadow-md backdrop-blur-sm transition hover:bg-background sm:block"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={scrollNext}
              aria-label="Next image"
              className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-border bg-background/90 p-2 text-foreground shadow-md backdrop-blur-sm transition hover:bg-background sm:block"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* Counter (mobile) */}
            <div className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-background/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-foreground backdrop-blur-sm sm:hidden">
              {selectedIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2 sm:mt-4 sm:gap-3">
          {images.map((img, i) => (
            <button
              key={`thumb-${img.src}-${i}`}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Show image ${i + 1}: ${img.alt}`}
              className={cn(
                "relative aspect-square overflow-hidden rounded-md bg-muted transition",
                selectedIndex === i
                  ? "ring-2 ring-primary"
                  : "border border-border/50 opacity-70 hover:opacity-100"
              )}
            >
              <img
                src={img.src}
                alt=""
                loading="lazy"
                decoding="async"
                draggable={false}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="h-[95vh] max-w-[95vw] border-0 bg-black/95 p-0">
          <div className="relative flex h-full w-full items-center justify-center">
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close"
                className="absolute right-3 top-3 z-50 h-11 w-11 rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            </DialogClose>

            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={lbPrev}
                  aria-label="Previous"
                  className="absolute left-3 top-1/2 z-40 -translate-y-1/2 rounded-full border border-amber-500/40 bg-black/60 p-3 text-amber-200 hover:bg-black/80"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={lbNext}
                  aria-label="Next"
                  className="absolute right-3 top-1/2 z-40 -translate-y-1/2 rounded-full border border-amber-500/40 bg-black/60 p-3 text-amber-200 hover:bg-black/80"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            <div
              className="flex h-full w-full items-center justify-center overflow-auto"
              style={{ touchAction: "pinch-zoom" }}
            >
              <img loading="lazy" decoding="async"
                key={images[lightboxIndex]?.src}
                src={images[lightboxIndex]?.src}
                alt={images[lightboxIndex]?.alt}
                className="max-h-full max-w-full select-none object-contain"
                draggable={false}
              />
            </div>

            <div className="absolute inset-x-0 bottom-4 text-center">
              <p className="inline-block rounded-full bg-black/60 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-200 backdrop-blur-sm">
                {lightboxIndex + 1} / {images.length}
                {images[lightboxIndex]?.title ? ` · ${images[lightboxIndex].title}` : ""}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductGallery;
